import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { Form, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import Title from '@/components/ui/Title';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { toast } from 'react-toastify';
import type { RootState } from '@/app/store';
import {
  createEvaluationQuestion,
  deleteEvaluationQuestion,
  fetchEvaluationTemplate,
  updateEvaluationQuestion,
  updateEvaluationTemplate,
} from '@/features/webinar/webinarThunks';
import type { EvaluationAnswerOptionInput, EvaluationQuestionType } from '@/features/webinar/types';
import Loader from '@/components/ui/Loader';

// Bornes des options en mode choix multiples (alignées sur le back : min 2, max 10).
const MIN_OPTIONS = 2;
const MAX_OPTIONS = 10;
const MAX_RATING_QUESTIONS = 5;

function createEvaluationSchema(t: (key: string) => string) {
  const ratingQuestionSchema = z.object({
    id: z.number().optional(),
    text: z
      .string()
      .min(1, t('evaluationForm.validation.questionRequired'))
      .refine((val) => val.trim().length > 0, {
        message: t('evaluationForm.validation.questionRequired'),
      })
      .refine((val) => val.length <= 300, {
        message: t('evaluationForm.validation.questionMaxLength'),
      }),
    type: z.string().min(1, t('evaluationForm.validation.questionRequired')),
  });

  // Champs du mode choix : laxistes ici, validés conditionnellement dans superRefine
  // pour ne pas bloquer l'enregistrement quand on est en mode notation.
  const optionSchema = z.object({
    id: z.number().optional(),
    textContent: z.string(),
  });

  const choiceQuestionSchema = z.object({
    id: z.number().optional(),
    text: z.string(),
    answerType: z.enum(['SINGLE_CHOICE', 'MULTIPLE_CHOICE']),
    options: z.array(optionSchema),
  });

  return z
    .object({
      evaluationTitle: z
        .string()
        .min(1, t('evaluationForm.validation.titleRequired'))
        .refine((val) => val.trim().length > 0, {
          message: t('evaluationForm.validation.titleRequired'),
        })
        .refine((val) => val.length <= 150, {
          message: t('evaluationForm.validation.questionMaxLength'),
        }),
      mode: z.enum(['RATING', 'MULTIPLE_CHOICE']),
      ratingQuestions: z.array(ratingQuestionSchema),
      choiceQuestion: choiceQuestionSchema,
    })
    .superRefine((data, ctx) => {
      if (data.mode === 'RATING') {
        if (data.ratingQuestions.length < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('evaluationForm.validation.minQuestions'),
            path: ['ratingQuestions'],
          });
        }
        if (data.ratingQuestions.length > MAX_RATING_QUESTIONS) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('evaluationForm.validation.maxQuestions'),
            path: ['ratingQuestions'],
          });
        }
        return;
      }

      // Mode choix multiples
      const question = data.choiceQuestion;
      if (question.text.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('evaluationForm.validation.questionRequired'),
          path: ['choiceQuestion', 'text'],
        });
      }
      if (question.text.length > 300) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('evaluationForm.validation.questionMaxLength'),
          path: ['choiceQuestion', 'text'],
        });
      }

      question.options.forEach((option, index) => {
        if (option.textContent.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('evaluationForm.validation.optionRequired'),
            path: ['choiceQuestion', 'options', index, 'textContent'],
          });
        }
        if (option.textContent.length > 300) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('evaluationForm.validation.optionMaxLength'),
            path: ['choiceQuestion', 'options', index, 'textContent'],
          });
        }
      });

      if (question.options.length < MIN_OPTIONS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('evaluationForm.validation.minOptions'),
          path: ['choiceQuestion', 'options'],
        });
      }
      if (question.options.length > MAX_OPTIONS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('evaluationForm.validation.maxOptions'),
          path: ['choiceQuestion', 'options'],
        });
      }
    });
}

type EvaluationFormValues = z.infer<ReturnType<typeof createEvaluationSchema>>;

type EvaluationThankYouFormProps = {
  workspaceId: number;
  webinarId: number;
  displayEvaluation?: boolean;
  onSyncThankYouPage?: () => Promise<void> | void;
  isEvergreen?: boolean;
};

export default function EvaluationThankYouForm({
  workspaceId,
  webinarId,
  displayEvaluation = false,
  onSyncThankYouPage,
  isEvergreen,
}: EvaluationThankYouFormProps) {
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const { evaluationTemplate } = useAppSelector((state: RootState) => state.webinar);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accordionValue, setAccordionValue] = useState<string | undefined>('');

  const hasEvaluation = Boolean(
    evaluationTemplate && evaluationTemplate.questions && evaluationTemplate.questions.length > 0,
  );

  const schema = useMemo(() => createEvaluationSchema(t), [t]);

  const choices = useMemo(
    () => [
      { label: t('evaluationForm.questionTypes.emoji'), value: 'SMILEYS' },
      { label: t('evaluationForm.questionTypes.thumbs'), value: 'THUMBS' },
      { label: t('evaluationForm.questionTypes.stars'), value: 'STARS' },
      { label: t('evaluationForm.questionTypes.text'), value: 'TEXT_AREA' },
    ],
    [t],
  );

  const defaultQuestions = useMemo(
    () => [
      { text: t('evaluationForm.defaultQuestions.connectionQuality'), type: 'SMILEYS' },
      { text: t('evaluationForm.defaultQuestions.clarity'), type: 'SMILEYS' },
      { text: t('evaluationForm.defaultQuestions.duration'), type: 'THUMBS' },
      { text: t('evaluationForm.defaultQuestions.satisfaction'), type: 'STARS' },
      { text: t('evaluationForm.defaultQuestions.suggestions'), type: 'TEXT_AREA' },
    ],
    [t],
  );

  const defaultChoiceQuestion = useMemo(
    () => ({
      text: '',
      answerType: 'SINGLE_CHOICE' as const,
      options: [{ textContent: '' }, { textContent: '' }],
    }),
    [],
  );

  const form = useForm<EvaluationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      evaluationTitle: '',
      mode: 'RATING',
      ratingQuestions: defaultQuestions,
      choiceQuestion: defaultChoiceQuestion,
    },
    mode: 'onChange',
  });

  const mode = form.watch('mode');

  useEffect(() => {
    if (workspaceId && webinarId) {
      dispatch(fetchEvaluationTemplate({ workspaceId, webinarId }));
    }
  }, [workspaceId, webinarId, dispatch]);

  useEffect(() => {
    if (!evaluationTemplate) {
      return;
    }

    const templateMode = evaluationTemplate.mode ?? 'RATING';
    const templateQuestions = evaluationTemplate.questions ?? [];

    if (templateMode === 'MULTIPLE_CHOICE') {
      const sourceQuestion = templateQuestions[0];
      form.reset({
        evaluationTitle: evaluationTemplate.title,
        mode: 'MULTIPLE_CHOICE',
        // On garde des questions de notation par défaut au cas où l'utilisateur repasse en mode notation.
        ratingQuestions: defaultQuestions,
        choiceQuestion: sourceQuestion
          ? {
              id: sourceQuestion.id,
              text: sourceQuestion.text,
              answerType:
                sourceQuestion.type === 'MULTIPLE_CHOICE' ? 'MULTIPLE_CHOICE' : 'SINGLE_CHOICE',
              options:
                sourceQuestion.options && sourceQuestion.options.length > 0
                  ? [...sourceQuestion.options]
                      .sort((a, b) => a.position - b.position)
                      .map((option) => ({ id: option.id, textContent: option.textContent }))
                  : defaultChoiceQuestion.options,
            }
          : defaultChoiceQuestion,
      });
      return;
    }

    form.reset({
      evaluationTitle: evaluationTemplate.title,
      mode: 'RATING',
      ratingQuestions:
        templateQuestions.length > 0
          ? templateQuestions.map((q) => ({ id: q.id, text: q.text, type: q.type }))
          : defaultQuestions,
      choiceQuestion: defaultChoiceQuestion,
    });
  }, [evaluationTemplate, form, defaultQuestions, defaultChoiceQuestion]);

  useEffect(() => {
    if (displayEvaluation) {
      setAccordionValue(isEvergreen ? '' : 'item-1');
    } else {
      setAccordionValue('');
    }
  }, [displayEvaluation, isEvergreen]);

  const {
    fields: ratingFields,
    append: appendQuestion,
    remove: removeRatingQuestion,
  } = useFieldArray({
    control: form.control,
    name: 'ratingQuestions',
  });

  const {
    fields: optionFields,
    append: appendOption,
    remove: removeOption,
  } = useFieldArray({
    control: form.control,
    name: 'choiceQuestion.options',
  });

  const addQuestion = () => {
    if (ratingFields.length < MAX_RATING_QUESTIONS) {
      appendQuestion({ text: '', type: '' });
    }
  };

  const removeQuestion = (index: number) => {
    if (ratingFields.length > 1) {
      removeRatingQuestion(index);
    }
  };

  const addOption = () => {
    if (optionFields.length < MAX_OPTIONS) {
      appendOption({ textContent: '' });
    }
  };

  const removeAnswerOption = (index: number) => {
    if (optionFields.length > MIN_OPTIONS) {
      removeOption(index);
    }
  };

  // Sauvegarde du mode notation : aligne le mode côté back puis réconcilie les questions.
  const saveRatingMode = async (data: EvaluationFormValues) => {
    const existingQuestions = evaluationTemplate?.questions ?? [];

    // 1. Passer le template en mode notation d'abord (lève le verrou de suppression côté back).
    await dispatch(
      updateEvaluationTemplate({
        workspaceId,
        webinarId,
        input: { title: data.evaluationTitle, mode: 'RATING' },
      }),
    ).unwrap();

    // 2. Supprimer les questions qui ne sont plus présentes (inclut une éventuelle question de choix).
    const submittedQuestionIds = data.ratingQuestions
      .map((q) => q.id)
      .filter((id): id is number => id !== undefined);

    for (const existingQuestion of existingQuestions) {
      if (!submittedQuestionIds.includes(existingQuestion.id)) {
        await dispatch(
          deleteEvaluationQuestion({ workspaceId, webinarId, questionId: existingQuestion.id }),
        ).unwrap();
      }
    }

    // 3. Créer / mettre à jour les questions de notation.
    for (let i = 0; i < data.ratingQuestions.length; i++) {
      const question = data.ratingQuestions[i];
      const existingQuestion = existingQuestions.find((eq) => eq.id === question.id);

      if (existingQuestion) {
        const hasChanged =
          existingQuestion.text !== question.text ||
          existingQuestion.type !== question.type ||
          existingQuestion.position !== i;

        if (hasChanged) {
          await dispatch(
            updateEvaluationQuestion({
              workspaceId,
              webinarId,
              questionId: existingQuestion.id,
              input: {
                text: question.text,
                type: question.type as EvaluationQuestionType,
                position: i,
              },
            }),
          ).unwrap();
        }
      } else {
        await dispatch(
          createEvaluationQuestion({
            workspaceId,
            webinarId,
            input: {
              text: question.text,
              type: question.type as EvaluationQuestionType,
              position: i,
            },
          }),
        ).unwrap();
      }
    }
  };

  // Sauvegarde du mode choix multiples : le back impose exactement 1 question de type choix
  // avec >= 2 options, et refuse le passage de mode tant que ce n'est pas le cas. On prépare
  // donc les questions AVANT de basculer le mode.
  //
  // On recrée systématiquement la question (plutôt que de la mettre à jour en place) : la maj
  // inline des options déclenche un 500 côté back (conflit de contrainte d'unicité lors du
  // clear+recreate des options). Le chemin "création" est, lui, fiable.
  const saveChoiceMode = async (data: EvaluationFormValues) => {
    const existingQuestions = evaluationTemplate?.questions ?? [];
    const choice = data.choiceQuestion;
    const options: EvaluationAnswerOptionInput[] = choice.options.map((option, index) => ({
      textContent: option.textContent,
      position: index,
    }));
    const questionInput = {
      text: choice.text,
      type: choice.answerType as EvaluationQuestionType,
      position: 0,
      options,
    };

    // 1. S'assurer que le template existe en mode RATING (upsert, aucune validation de mode,
    //    et lève le verrou de suppression de question si on était déjà en mode choix).
    await dispatch(
      updateEvaluationTemplate({
        workspaceId,
        webinarId,
        input: { title: data.evaluationTitle, mode: 'RATING' },
      }),
    ).unwrap();
    // 2. Supprimer librement les anciennes questions (le template est en RATING).
    for (const existingQuestion of existingQuestions) {
      await dispatch(
        deleteEvaluationQuestion({ workspaceId, webinarId, questionId: existingQuestion.id }),
      ).unwrap();
    }
    // 3. Créer l'unique question de choix avec ses options.
    await dispatch(
      createEvaluationQuestion({ workspaceId, webinarId, input: questionInput }),
    ).unwrap();
    // 4. Basculer le template en mode choix multiples (le back valide alors 1 question + >= 2 options).
    await dispatch(
      updateEvaluationTemplate({
        workspaceId,
        webinarId,
        input: { title: data.evaluationTitle, mode: 'MULTIPLE_CHOICE' },
      }),
    ).unwrap();
  };

  const onSubmit = async (data: EvaluationFormValues) => {
    setIsSubmitting(true);
    try {
      if (data.mode === 'MULTIPLE_CHOICE') {
        await saveChoiceMode(data);
      } else {
        await saveRatingMode(data);
      }

      await dispatch(fetchEvaluationTemplate({ workspaceId, webinarId })).unwrap();
      await onSyncThankYouPage?.();
      toast.success(t('evaluationForm.saveSuccess'));
      setAccordionValue('');
    } catch (error) {
      console.error('Error saving evaluation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Accordion
          type="single"
          collapsible
          value={accordionValue}
          onValueChange={setAccordionValue}
          className="bg-medium rounded-[20px] p-4"
        >
          <AccordionItem value="item-1" className="bg-medium">
            <div className="flex flex-col w-full">
              <div className="flex items-start gap-2 w-full">
                <div className="flex-1">
                  <FormField
                    name="evaluationTitle"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <TextFieldWithLimit
                          label={t('evaluationForm.privateTitle')}
                          value={field.value}
                          onChange={(v) => field.onChange(v)}
                          maxLength={150}
                          className="bg-white"
                          error={fieldState.error?.message}
                        />
                      </FormItem>
                    )}
                  />
                </div>
                <AccordionTrigger
                  className="rounded-[20px] h-14 p-0 block"
                  fillForm={hasEvaluation}
                />
              </div>
            </div>
            <AccordionContent className="flex flex-col gap-4 rounded-[20px] p-0 pt-4">
              <Title level={2}>{t('evaluationForm.sectionTitle')}</Title>

              {/* Sélecteur de mode : notation ou question à choix multiples */}
              <FormField
                control={form.control}
                name="mode"
                render={({ field }) => (
                  <FormItem>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger label={t('evaluationForm.modeLabel')} className="w-full h-14">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="RATING">{t('evaluationForm.modes.rating')}</SelectItem>
                        <SelectItem value="MULTIPLE_CHOICE">
                          {t('evaluationForm.modes.multipleChoice')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <Separator className="bg-input" />

              {mode === 'RATING' ? (
                <>
                  {ratingFields.map((field, index) => (
                    <div key={field.id} className="flex flex-col gap-2">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <FormField
                            control={form.control}
                            name={`ratingQuestions.${index}.text`}
                            render={({ field: textField, fieldState }) => (
                              <TextFieldWithLimit
                                label={t('evaluationForm.questionLabel', { index: index + 1 })}
                                value={textField.value ?? ''}
                                onChange={(v) => textField.onChange(v)}
                                maxLength={300}
                                error={fieldState.error?.message}
                              />
                            )}
                          />
                        </div>

                        <FormField
                          control={form.control}
                          name={`ratingQuestions.${index}.type`}
                          render={({ field: typeField }) => (
                            <FormItem>
                              <Select value={typeField.value} onValueChange={typeField.onChange}>
                                <SelectTrigger
                                  label={t('evaluationForm.typeLabel')}
                                  className="w-[250px] h-14"
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {choices.map((choice) => (
                                    <SelectItem key={choice.value} value={choice.value}>
                                      {choice.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormItem>
                          )}
                        />

                        {ratingFields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeQuestion(index)}
                            className="bg-white rounded-lg h-14 py-3 px-2 border border-input cursor-pointer hover:bg-gray-50"
                          >
                            <Trash2 className="size-6" />
                          </button>
                        )}
                      </div>
                      <Separator className="bg-input" />
                    </div>
                  ))}

                  <div className="flex items-center gap-2">
                    {ratingFields.length < MAX_RATING_QUESTIONS && (
                      <button
                        type="button"
                        onClick={addQuestion}
                        className="flex items-center gap-2 pt-4 cursor-pointer"
                      >
                        <Plus className="size-6" />
                        <Label className="cursor-pointer text-sm">
                          {t('evaluationForm.addQuestion')}
                        </Label>
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Mode question à choix multiples : une seule question + options */}
                  <div className="flex items-start gap-2 w-full flex-col lg:flex-row">
                    <div className="w-full lg:w-2/3">
                      <FormField
                        control={form.control}
                        name="choiceQuestion.text"
                        render={({ field, fieldState }) => (
                          <TextFieldWithLimit
                            label={t('evaluationForm.choiceQuestionLabel')}
                            value={field.value ?? ''}
                            onChange={(v) => field.onChange(v)}
                            maxLength={300}
                            error={fieldState.error?.message}
                          />
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="choiceQuestion.answerType"
                      render={({ field }) => (
                        <FormItem className="w-full lg:w-1/3">
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger
                              label={t('evaluationForm.answerTypeLabel')}
                              className="w-full h-14"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="SINGLE_CHOICE">
                                {t('evaluationForm.answerTypes.single')}
                              </SelectItem>
                              <SelectItem value="MULTIPLE_CHOICE">
                                {t('evaluationForm.answerTypes.multiple')}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-4">
                    {optionFields.map((field, index) => (
                      <div key={field.id} className="flex items-start gap-2">
                        <div className="flex-1">
                          <FormField
                            control={form.control}
                            name={`choiceQuestion.options.${index}.textContent`}
                            render={({ field: optionField, fieldState }) => (
                              <TextFieldWithLimit
                                label={t('evaluationForm.answerLabel', { index: index + 1 })}
                                value={optionField.value ?? ''}
                                onChange={(v) => optionField.onChange(v)}
                                maxLength={300}
                                error={fieldState.error?.message}
                              />
                            )}
                          />
                        </div>

                        {optionFields.length > MIN_OPTIONS && (
                          <button
                            type="button"
                            onClick={() => removeAnswerOption(index)}
                            className="bg-white rounded-lg h-14 py-3 px-2 border border-input cursor-pointer hover:bg-gray-50"
                          >
                            <Trash2 className="size-6" />
                          </button>
                        )}
                      </div>
                    ))}

                    {/* Message d'erreur global sur la liste d'options (min/max) */}
                    <FormField
                      control={form.control}
                      name="choiceQuestion.options"
                      render={() => (
                        <FormItem>
                          <FormMessage className="text-left text-red-500 text-sm ml-0" />
                        </FormItem>
                      )}
                    />

                    {optionFields.length < MAX_OPTIONS && (
                      <>
                        <button
                          type="button"
                          onClick={addOption}
                          className="flex items-center gap-2 pt-2 cursor-pointer"
                        >
                          <Plus className="size-6" />
                          <Label className="cursor-pointer text-sm">
                            {t('evaluationForm.addAnswer')}
                          </Label>
                        </button>
                        <Separator className="bg-input" />
                      </>
                    )}
                  </div>
                </>
              )}

              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting || !form.formState.isValid}
                  className="w-full disabled:bg-gray-200 disabled:text-gray-500"
                >
                  {isSubmitting && <Loader />}
                  {t('evaluationForm.save')}
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </form>
    </Form>
  );
}
